import { Injectable } from '@angular/core';
import { NotificationGroupService } from './notification-group.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as ObjectActions from '../../settings/notification-group/notification-group.actions';
import { catchError, of, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';
import { LogbookAppState } from '../../logbook.reducer';
import * as AppActions from '../../app/actions';
import {
  IAddModalDataResponse,
  IEditModalDataResponse,
  INotificationGroupResponseData,
  IUserDataResponse,
} from './notification-group.model';
import { ServiceUtilities } from '../../../shared/helper/service-utilities';
import {
  IBaseOneResponse,
  IBulkResponseRecord,
  IGetManyResponse,
} from '../../../shared/model/interface/crud-response-interface.model';
import _ from 'lodash';

@Injectable()
export class NotificationGroupEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<LogbookAppState>,
    private readonly notificationGroupService: NotificationGroupService,
  ) {}

  getNotificationGroupData = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.NOTIFICATION_GROUP_DATA_LOADING),
      switchMap((payload: ObjectActions.NotificationGroupDataLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.notificationGroupService
          .getNotificationGroupData(
            ServiceUtilities.prepareGenericHttpParamsForRequest({
              page: payload.tableQuery.page,
              rowsPerPage: payload.tableQuery.rowsPerPage,
              sort: payload.tableQuery.sort,
              ...(!_.isNil(payload.tableQuery.isActive) ? { isActive: Boolean(payload.tableQuery.isActive) } : {}),
              ...(payload.tableQuery.search ? { search: payload.tableQuery.search } : {}),
            }),
          )
          .pipe(
            switchMap((response: IGetManyResponse<INotificationGroupResponseData>) => {
              return of(new ObjectActions.NotificationGroupDataLoaded(response), new AppActions.HideLoader());
            }),
            catchError((errorRes) => {
              return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
            }),
          );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getNotificationGroupRecipientInfo = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.NOTIFICATION_GROUP_RECIPIENT_INFO_LOADING),
      switchMap((payload: ObjectActions.GetRecipientInfoLoading) => {
        this.store.dispatch(new AppActions.ShowTopLoader());

        return this.notificationGroupService
          .getNotificationGroupRecipientInfo({ userIds: payload.userIds, isActive: true })
          .pipe(
            switchMap((response: IGetManyResponse<IUserDataResponse>) => {
              return of(new ObjectActions.GetRecipientInfoLoaded(response), new AppActions.HideTopLoader());
            }),
            catchError((errorRes) => {
              return of(new ObjectActions.FetchError(errorRes), new AppActions.HideTopLoader());
            }),
          );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideTopLoader());
      }),
    ),
  );

  getAddModalData = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.NOTIFICATION_GROUP_ADD_MODAL_DATA_LOADING),
      switchMap((payload: ObjectActions.GetNotificationGroupAddModalDataLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.notificationGroupService.getAddModalData().pipe(
          switchMap((response: IAddModalDataResponse) => {
            return of(new ObjectActions.GetNotificationGroupAddModalDataLoaded(response), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getEditModalData = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.NOTIFICATION_GROUP_EDIT_MODAL_DATA_LOADING),
      switchMap((payload: ObjectActions.GetNotificationGroupEditModalDataLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.notificationGroupService.getEditModalData(payload.mailGroupId).pipe(
          switchMap((response: IEditModalDataResponse) => {
            return of(new ObjectActions.GetNotificationGroupEditModalDataLoaded(response), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  addNotificationGroup = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.NOTIFICATION_GROUP_ADD),
      switchMap((objectData: ObjectActions.AddNotificationGroup) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.notificationGroupService.addNotificationGroup(objectData.payload, objectData.issuer).pipe(
          switchMap((response: IBaseOneResponse<INotificationGroupResponseData>) => {
            return of(new ObjectActions.AddNotificationGroupCompleted(response), new AppActions.HideLoader());
          }),
          catchError((error) => {
            return of(new ObjectActions.FetchError(error), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  editNotificationGroup = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.NOTIFICATION_GROUP_EDIT),
      switchMap((objectData: ObjectActions.EditNotificationGroup) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.notificationGroupService
          .editNotificationGroup(objectData.payload, objectData.issuerAndReason, objectData.id)
          .pipe(
            switchMap((response: IBaseOneResponse<INotificationGroupResponseData>) => {
              return of(new ObjectActions.EditNotificationGroupCompleted(response), new AppActions.HideLoader());
            }),
            catchError((error) => {
              return of(new ObjectActions.FetchError(error), new AppActions.HideLoader());
            }),
          );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  bulkEditNotificationGroup = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.NOTIFICATION_GROUP_BULK_EDIT),
      switchMap((objectData: ObjectActions.BulkEditNotificationGroup) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.notificationGroupService
          .bulkEditNotificationGroup(objectData.payload, objectData.issuerAndReason)
          .pipe(
            switchMap((response: IGetManyResponse<IBulkResponseRecord<INotificationGroupResponseData>>) => {
              return of(new ObjectActions.BulkEditNotificationGroupCompleted(response), new AppActions.HideLoader());
            }),
            catchError((error) => {
              return of(new ObjectActions.FetchError(error), new AppActions.HideLoader());
            }),
          );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  deleteOrBulkDeleteNotificationGroup = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.NOTIFICATION_GROUP_DELETE_OR_BULK_DELETE),
      switchMap((objectData: ObjectActions.DeleteOrBulkDeleteNotificationGroup) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.notificationGroupService
          .deleteOrBulkDeleteNotificationGroup(objectData.notificationGroupIds, objectData.issuerAndReason)
          .pipe(
            switchMap((response: IGetManyResponse<IBulkResponseRecord<INotificationGroupResponseData>>) => {
              return of(
                new ObjectActions.DeleteOrBulkDeleteNotificationGroupCompleted(response),
                new AppActions.HideLoader(),
              );
            }),
            catchError((error) => {
              return of(new ObjectActions.FetchError(error), new AppActions.HideLoader());
            }),
          );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );
}
